import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Button, Input, Logo } from '@artosera/ui';
import { ApiError, connexion } from '../lib/api.js';
import { raisonErreur } from '../lib/modules.js';
import { CLE_SESSION, useSession } from '../auth/useSession.js';
import styles from './Login.module.css';

/**
 * Connexion + 2FA (docs/dev/api.md §3, arborescence §2). Un seul écran, deux
 * étapes : identifiants, puis code si `deuxiemeFacteurRequis` revient.
 */
export function Login(): JSX.Element {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { moi, enChargement } = useSession();

  // Un titre d'onglet propre à l'écran, comme les autres pages de la console.
  useEffect(() => {
    document.title = t('session.titreDocument', { page: t('connexion.titreOnglet') });
  }, [t]);

  /** Où aller après la connexion : l'adresse demandée avant, sinon la vue d'aigle. */
  const adresseDemandee = (location.state as { depuis?: string } | null)?.depuis;

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [etape, setEtape] = useState<'identifiants' | 'deuxiemeFacteur'>('identifiants');
  const [code, setCode] = useState('');
  const [codeSecours, setCodeSecours] = useState('');
  const [utiliserCodeSecours, setUtiliserCodeSecours] = useState(false);

  const mutation = useMutation({
    mutationFn: connexion,
    onSuccess: async (reponse) => {
      if (reponse.deuxiemeFacteurRequis) {
        setEtape('deuxiemeFacteur');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: CLE_SESSION });
      // Confinée à l'enrôlement : seul l'écran de sécurité s'ouvre.
      navigate(reponse.enrolementMfaSeulement ? '/securite' : (adresseDemandee ?? '/vue-d-aigle'), {
        replace: true,
      });
    },
  });

  // Déjà connecté : on rend la destination demandée, jamais la vue d'aigle par
  // défaut — l'invalidation de `/me` ci-dessus fait apparaître la session
  // AVANT que `navigate` ne parte, et la redirection perdait l'adresse.
  if (!enChargement && moi) {
    const destination = moi.enrolementMfaSeulement
      ? '/securite'
      : (adresseDemandee ?? '/vue-d-aigle');
    return <Navigate to={destination} replace />;
  }

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    mutation.mutate({
      email,
      motDePasse,
      ...(etape === 'deuxiemeFacteur' && !utiliserCodeSecours ? { codeTotp: code } : {}),
      ...(etape === 'deuxiemeFacteur' && utiliserCodeSecours ? { codeSecours } : {}),
    });
  };

  const erreur = mutation.error instanceof ApiError ? mutation.error : null;
  // L'API envoie le code du catalogue (`auth.identifiants_invalides`) et la
  // raison fine dans `details.raison` : on teste les deux, jamais le seul
  // `code` nu, qui n'existe pas côté serveur.
  const raison = raisonErreur(erreur);
  const messageErreur =
    erreur === null
      ? null
      : raison === 'compte_verrouille'
        ? t('connexion.erreurVerrouille')
        : raison === 'compte_desactive'
          ? t('connexion.erreurDesactive')
          : erreur.code === 'auth.identifiants_invalides' || raison === 'identifiants_invalides'
            ? t('connexion.erreurIdentifiants')
            : t('connexion.erreurGenerique');

  return (
    <div className={styles.ecran}>
      <div className={styles.carte}>
        <div className={styles.marque}>
          <Logo title="Artosera" />
        </div>
        <h1 className={styles.titre}>{t('connexion.titre')}</h1>
        <p className={styles.sousTitre}>{t('connexion.sousTitre')}</p>

        {etape === 'identifiants' ? (
          <form className={styles.formulaire} onSubmit={onSubmit}>
            {messageErreur ? (
              <p className={styles.erreur} role="alert">
                {messageErreur}
              </p>
            ) : null}
            <div className={styles.champ}>
              <label htmlFor="connexion-email">{t('connexion.champEmail')}</label>
              <Input
                id="connexion-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className={styles.champ}>
              <label htmlFor="connexion-mot-de-passe">{t('connexion.champMotDePasse')}</label>
              <Input
                id="connexion-mot-de-passe"
                type="password"
                autoComplete="current-password"
                required
                value={motDePasse}
                onChange={(event) => setMotDePasse(event.target.value)}
              />
            </div>
            <Button type="submit" loading={mutation.isPending}>
              {mutation.isPending ? t('connexion.enCours') : t('connexion.bouton')}
            </Button>
          </form>
        ) : (
          <form className={styles.formulaire} onSubmit={onSubmit}>
            <h2 className={styles.titre}>{t('connexion.deuxiemeFacteurTitre')}</h2>
            <p>{t('connexion.deuxiemeFacteurTexte')}</p>
            {messageErreur ? (
              <p className={styles.erreur} role="alert">
                {messageErreur}
              </p>
            ) : null}
            {utiliserCodeSecours ? (
              <div className={styles.champ}>
                <label htmlFor="connexion-code-secours">{t('connexion.champCodeSecours')}</label>
                <Input
                  id="connexion-code-secours"
                  required
                  value={codeSecours}
                  onChange={(event) => setCodeSecours(event.target.value)}
                />
              </div>
            ) : (
              <div className={styles.champ}>
                <label htmlFor="connexion-code-totp">{t('connexion.champCodeTotp')}</label>
                <Input
                  id="connexion-code-totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
            )}
            <Button type="submit" loading={mutation.isPending}>
              {mutation.isPending ? t('connexion.enCours') : t('connexion.valider')}
            </Button>
            <button
              type="button"
              className={styles.lienDiscret}
              onClick={() => setUtiliserCodeSecours((valeur) => !valeur)}
            >
              {utiliserCodeSecours
                ? t('connexion.utiliserTotp')
                : t('connexion.utiliserCodeSecours')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
